// src/Services/AdminService/AdminCertificationService.ts
import httpClient from '@/services/httpClient';
import axios from 'axios';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

/**
 * Standard API response wrapper from backend
 * All API endpoints return this structure: { success, data, message }
 */
interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
  pagination?: PaginationInfo;
}

/**
 * Pagination information
 */
export interface PaginationInfo {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

/**
 * Certification entity
 */
export interface Certification {
  id: number;
  certification_name: string;
  description?: string;
  validity_period?: number; // in days
  validity_type: 'lifetime' | 'limited';
  template_file_path?: string;
  status: 'active' | 'inactive';
  created_at: string;
  updated_at: string;
  required_courses_count?: number;
  created_by_name?: string;
  created_by_lastname?: string;
}

/**
 * Certification with full course details
 */
export interface CertificationWithCourses extends Certification {
  required_courses: RequiredCourse[];
}

/**
 * Required course for certification
 */
export interface RequiredCourse {
  requirement_id: number;
  course_id: number;
  course_title: string;
  short_description?: string;
  thumbnail?: string;
  course_duration?: string;
  level: string;
  course_status: string;
  category_name?: string;
  subcategory_name?: string;
}

/**
 * Active course (for dropdown)
 */
export interface ActiveCourse {
  id: number;
  title: string;
  short_description?: string;
  thumbnail?: string;
  course_duration?: string;
  level: string;
  category_name?: string;
  subcategory_name?: string;
}

/**
 * Certification creation/update payload
 */
export interface CertificationPayload {
  certification_name: string;
  description?: string;
  validity_period?: number;
  validity_type?: 'lifetime' | 'limited';
  template_file_path?: string;
  status?: 'active' | 'inactive';
  course_ids?: number[];
}

/**
 * Certifications list response
 */
export interface CertificationsListResponse {
  data: Certification[];
  pagination: PaginationInfo;
}

/**
 * Learner progress entry
 */
export interface LearnerProgress {
  enrollment_id: number;
  certification_name: string;
  student_name: string;
  student_email: string;
  enrollment_date: string;
  target_completion_date?: string;
  total_required_courses: number;
  completed_courses: number;
  progress_percentage: number;
  status: 'in_progress' | 'completed' | 'expired' | 'revoked';
  certificate_file_path?: string | null;
}

/**
 * Learner progress list response
 */
export interface LearnerProgressListResponse {
  data: LearnerProgress[];
  pagination: PaginationInfo;
}

/**
 * Expiry/Renewal entry
 */
export interface ExpiryRenewal {
  enrollment_id: number;
  certification_name: string;
  student_name: string;
  student_email: string;
  certificate_issued_date: string;
  expiry_date: string;
  days_until_expiry: number;
  status: 'expiring_soon' | 'expired';
}

/**
 * Expiry renewals list response
 */
export interface ExpiryRenewalsListResponse {
  data: ExpiryRenewal[];
  pagination: PaginationInfo;
}

/**
 * Enrollment payload
 */
export interface EnrollmentPayload {
  certification_id: number;
  user_id: string;
}

/**
 * Issue certificate response
 */
export interface IssueCertificateResponse {
  success: boolean;
  message: string;
  completed_courses?: number;
  total_required_courses?: number;
  missing_courses?: Array<{
    course_id: number;
    course_title: string;
  }>;
}

// ============================================================================
// API ENDPOINTS
// ============================================================================

const ENDPOINTS = {
  // Certification CRUD
  GET_ALL_CERTIFICATIONS: '/admin/certifications',
  GET_CERTIFICATION_BY_ID: (certificationId: number) => `/admin/certifications/${certificationId}`,
  CREATE_CERTIFICATION: '/admin/certifications',
  UPDATE_CERTIFICATION: (certificationId: number) => `/admin/certifications/${certificationId}`,
  DELETE_CERTIFICATION: (certificationId: number) => `/admin/certifications/${certificationId}`,

  // Certification status
  TOGGLE_STATUS: (certificationId: number) => `/admin/certifications/${certificationId}/toggle-status`,

  // Course requirements
  ADD_COURSE_REQUIREMENT: (certificationId: number) => `/admin/certifications/${certificationId}/courses`,
  REMOVE_COURSE_REQUIREMENT: (certificationId: number, courseId: number) =>
    `/admin/certifications/${certificationId}/courses/${courseId}`,

  // Metadata
  GET_ACTIVE_COURSES: '/admin/certifications/metadata/active-courses',

  // Learner Progress & Tracking
  GET_LEARNER_PROGRESS: '/admin/certifications/learner-progress',

  // Expiry & Renewals
  GET_EXPIRY_RENEWALS: '/admin/certifications/expiry-renewals',

  // Enrollment Management
  ENROLL_STUDENT: '/admin/certifications/enroll',
  ISSUE_CERTIFICATE: (enrollmentId: number) => `/admin/certifications/${enrollmentId}/issue`,

  // Certificate Viewing & Downloading
  VIEW_CERTIFICATE: (enrollmentId: number) => `/admin/certifications/enrollment/${enrollmentId}/certificate/view`,
  DOWNLOAD_CERTIFICATE: (enrollmentId: number) => `/admin/certifications/enrollment/${enrollmentId}/certificate/download`,
};

// ============================================================================
// SERVICE FUNCTIONS - Certification CRUD
// ============================================================================

/**
 * Get all certifications with pagination and filters
 * @param page - Page number (default: 1)
 * @param limit - Items per page (default: 10)
 * @param status - Filter by status (active, inactive, or all)
 * @param search - Search by certification name
 */
export const getAllCertifications = async (
  page: number = 1,
  limit: number = 10,
  status?: 'active' | 'inactive' | 'all',
  search?: string
): Promise<{
  success: boolean;
  data?: CertificationsListResponse;
  error?: { message: string };
}> => {
  try {
    // Build query parameters
    const params: any = { page, limit };
    if (status && status !== 'all') params.status = status;
    if (search) params.search = search;

    const queryString = new URLSearchParams(params).toString();
    const url = `${ENDPOINTS.GET_ALL_CERTIFICATIONS}?${queryString}`;

    const response = await httpClient.get<ApiResponse<Certification[]>>(url);

    return {
      success: true,
      data: {
        data: response.data || [],
        pagination: response.pagination || {
          total: 0,
          page: 1,
          limit: 10,
          totalPages: 0,
        },
      },
      error: undefined,
    };
  } catch (error: any) {
    return {
      success: false,
      error: {
        message: error.response?.data?.message || error.message || 'Failed to fetch certifications',
      },
    };
  }
};

/**
 * Get certification by ID with required courses
 * @param certificationId - ID of the certification
 */
export const getCertificationById = async (
  certificationId: number
): Promise<{
  success: boolean;
  data?: CertificationWithCourses;
  error?: { message: string };
}> => {
  try {
    const response = await httpClient.get<ApiResponse<CertificationWithCourses>>(
      ENDPOINTS.GET_CERTIFICATION_BY_ID(certificationId)
    );

    return {
      success: true,
      data: response.data,
      error: undefined,
    };
  } catch (error: any) {
    return {
      success: false,
      error: {
        message: error.response?.data?.message || error.message || 'Failed to fetch certification details',
      },
    };
  }
};

/**
 * Create a new certification
 * @param certificationData - Certification creation data
 */
export const createCertification = async (
  certificationData: CertificationPayload
): Promise<{
  success: boolean;
  data?: { certificationId: number; message: string };
  error?: { message: string };
}> => {
  try {
    const response = await httpClient.post<ApiResponse<{ certificationId: number; message: string }>>(
      ENDPOINTS.CREATE_CERTIFICATION,
      certificationData
    );

    return {
      success: true,
      data: response.data,
      error: undefined,
    };
  } catch (error: any) {
    return {
      success: false,
      error: {
        message: error.response?.data?.message || error.message || 'Failed to create certification',
      },
    };
  }
};

/**
 * Update an existing certification
 * @param certificationId - ID of the certification to update
 * @param certificationData - Updated certification data
 */
export const updateCertification = async (
  certificationId: number,
  certificationData: Partial<CertificationPayload>
): Promise<{
  success: boolean;
  data?: { message: string };
  error?: { message: string };
}> => {
  try {
    const response = await httpClient.put<ApiResponse<{ message: string }>>(
      ENDPOINTS.UPDATE_CERTIFICATION(certificationId),
      certificationData
    );

    return {
      success: true,
      data: response.data,
      error: undefined,
    };
  } catch (error: any) {
    return {
      success: false,
      error: {
        message: error.response?.data?.message || error.message || 'Failed to update certification',
      },
    };
  }
};

/**
 * Delete a certification
 * @param certificationId - ID of the certification to delete
 */
export const deleteCertification = async (
  certificationId: number
): Promise<{
  success: boolean;
  error?: { message: string };
}> => {
  try {
    await httpClient.delete<ApiResponse<null>>(
      ENDPOINTS.DELETE_CERTIFICATION(certificationId)
    );

    return {
      success: true,
      error: undefined,
    };
  } catch (error: any) {
    return {
      success: false,
      error: {
        message: error.response?.data?.message || error.message || 'Failed to delete certification',
      },
    };
  }
};

// ============================================================================
// SERVICE FUNCTIONS - Certification Status
// ============================================================================

/**
 * Toggle certification status (active <-> inactive)
 * @param certificationId - ID of the certification
 */
export const toggleCertificationStatus = async (
  certificationId: number
): Promise<{
  success: boolean;
  data?: { status: 'active' | 'inactive'; message: string };
  error?: { message: string };
}> => {
  try {
    const response = await httpClient.patch<ApiResponse<{ status: 'active' | 'inactive'; message: string }>>(
      ENDPOINTS.TOGGLE_STATUS(certificationId)
    );

    return {
      success: true,
      data: response.data,
      error: undefined,
    };
  } catch (error: any) {
    return {
      success: false,
      error: {
        message: error.response?.data?.message || error.message || 'Failed to toggle certification status',
      },
    };
  }
};

// ============================================================================
// SERVICE FUNCTIONS - Course Requirements
// ============================================================================

/**
 * Add a course requirement to a certification
 * @param certificationId - ID of the certification
 * @param courseId - ID of the course to add
 */
export const addCourseRequirement = async (
  certificationId: number,
  courseId: number
): Promise<{
  success: boolean;
  data?: { requirementId: number; message: string };
  error?: { message: string };
}> => {
  try {
    const response = await httpClient.post<ApiResponse<{ requirementId: number; message: string }>>(
      ENDPOINTS.ADD_COURSE_REQUIREMENT(certificationId),
      { course_id: courseId }
    );

    return {
      success: true,
      data: response.data,
      error: undefined,
    };
  } catch (error: any) {
    return {
      success: false,
      error: {
        message: error.response?.data?.message || error.message || 'Failed to add course requirement',
      },
    };
  }
};

/**
 * Remove a course requirement from a certification
 * @param certificationId - ID of the certification
 * @param courseId - ID of the course to remove
 */
export const removeCourseRequirement = async (
  certificationId: number,
  courseId: number
): Promise<{
  success: boolean;
  error?: { message: string };
}> => {
  try {
    await httpClient.delete<ApiResponse<null>>(
      ENDPOINTS.REMOVE_COURSE_REQUIREMENT(certificationId, courseId)
    );

    return {
      success: true,
      error: undefined,
    };
  } catch (error: any) {
    return {
      success: false,
      error: {
        message: error.response?.data?.message || error.message || 'Failed to remove course requirement',
      },
    };
  }
};

// ============================================================================
// SERVICE FUNCTIONS - Metadata
// ============================================================================

/**
 * Get all active courses (for dropdown in create/edit certification)
 */
export const getActiveCourses = async (): Promise<{
  success: boolean;
  data?: ActiveCourse[];
  error?: { message: string };
}> => {
  try {
    const response = await httpClient.get<ApiResponse<ActiveCourse[]>>(
      ENDPOINTS.GET_ACTIVE_COURSES
    );

    return {
      success: true,
      data: response.data || [],
      error: undefined,
    };
  } catch (error: any) {
    return {
      success: false,
      error: {
        message: error.response?.data?.message || error.message || 'Failed to fetch active courses',
      },
    };
  }
};

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Format validity for display
 * @param validity_type - Type of validity
 * @param validity_period - Period in days
 */
export const formatValidity = (
  validity_type: 'lifetime' | 'limited',
  validity_period?: number
): string => {
  if (validity_type === 'lifetime') {
    return 'Lifetime';
  }
  if (validity_period) {
    if (validity_period === 1) return '1 day';
    if (validity_period < 30) return `${validity_period} days`;
    if (validity_period === 30) return '1 month';
    if (validity_period < 365) return `${Math.floor(validity_period / 30)} months`;
    if (validity_period === 365) return '1 year';
    return `${Math.floor(validity_period / 365)} years`;
  }
  return 'N/A';
};

/**
 * Get status badge color
 * @param status - Certification status
 */
export const getStatusBadgeColor = (
  status: 'active' | 'inactive'
): 'success' | 'default' => {
  return status === 'active' ? 'success' : 'default';
};

// ============================================================================
// SERVICE FUNCTIONS - Learner Progress & Tracking
// ============================================================================

/**
 * Get learner progress (students working on certifications)
 * @param certificationId - Filter by certification ID (optional)
 * @param status - Filter by enrollment status (optional)
 * @param search - Search by student name (optional)
 * @param page - Page number (default: 1)
 * @param limit - Items per page (default: 10)
 */
export const getLearnerProgress = async (
  certificationId?: number | null,
  status?: string | null,
  search?: string | null,
  page: number = 1,
  limit: number = 10
): Promise<{
  success: boolean;
  data?: LearnerProgressListResponse;
  error?: { message: string };
}> => {
  try {
    // Build query parameters
    const params: any = { page, limit };
    if (certificationId) params.certification_id = certificationId;
    if (status) params.status = status;
    if (search) params.search = search;

    const queryString = new URLSearchParams(params).toString();
    const url = `${ENDPOINTS.GET_LEARNER_PROGRESS}?${queryString}`;

    const response = await httpClient.get<ApiResponse<LearnerProgress[]>>(url);

    return {
      success: true,
      data: {
        data: response.data || [],
        pagination: response.pagination || {
          total: 0,
          page: 1,
          limit: 10,
          totalPages: 0,
        },
      },
      error: undefined,
    };
  } catch (error: any) {
    return {
      success: false,
      error: {
        message: error.response?.data?.message || error.message || 'Failed to fetch learner progress',
      },
    };
  }
};

// ============================================================================
// SERVICE FUNCTIONS - Expiry & Renewals
// ============================================================================

/**
 * Get expiring and expired certifications
 * @param filter - Filter by 'expiring_soon', 'expired', or 'all' (default: 'expiring_soon')
 * @param page - Page number (default: 1)
 * @param limit - Items per page (default: 10)
 */
export const getExpiryRenewals = async (
  filter: 'expiring_soon' | 'expired' | 'all' = 'expiring_soon',
  page: number = 1,
  limit: number = 10
): Promise<{
  success: boolean;
  data?: ExpiryRenewalsListResponse;
  error?: { message: string };
}> => {
  try {
    // Build query parameters
    const params: any = { page, limit, filter };

    const queryString = new URLSearchParams(params).toString();
    const url = `${ENDPOINTS.GET_EXPIRY_RENEWALS}?${queryString}`;

    const response = await httpClient.get<ApiResponse<ExpiryRenewal[]>>(url);

    return {
      success: true,
      data: {
        data: response.data || [],
        pagination: response.pagination || {
          total: 0,
          page: 1,
          limit: 10,
          totalPages: 0,
        },
      },
      error: undefined,
    };
  } catch (error: any) {
    return {
      success: false,
      error: {
        message: error.response?.data?.message || error.message || 'Failed to fetch expiry renewals',
      },
    };
  }
};

// ============================================================================
// SERVICE FUNCTIONS - Enrollment Management
// ============================================================================

/**
 * Enroll a student in a certification
 * @param certificationId - ID of the certification
 * @param userId - UUID of the student
 */
export const enrollStudent = async (
  certificationId: number,
  userId: string
): Promise<{
  success: boolean;
  data?: { enrollmentId: number; message: string };
  error?: { message: string };
}> => {
  try {
    const response = await httpClient.post<ApiResponse<{ enrollmentId: number; message: string }>>(
      ENDPOINTS.ENROLL_STUDENT,
      {
        certification_id: certificationId,
        user_id: userId,
      }
    );

    return {
      success: true,
      data: response.data,
      error: undefined,
    };
  } catch (error: any) {
    return {
      success: false,
      error: {
        message: error.response?.data?.message || error.message || 'Failed to enroll student',
      },
    };
  }
};

/**
 * Issue certificate to enrolled student (checks requirements and issues if met)
 * @param enrollmentId - ID of the enrollment
 */
export const issueCertificate = async (
  enrollmentId: number
): Promise<{
  success: boolean;
  data?: IssueCertificateResponse;
  error?: { message: string };
}> => {
  try {
    const response = await httpClient.post<IssueCertificateResponse>(
      ENDPOINTS.ISSUE_CERTIFICATE(enrollmentId)
    );

    return {
      success: response.success,
      data: response,
      error: undefined,
    };
  } catch (error: any) {
    return {
      success: false,
      error: {
        message: error.response?.data?.message || error.message || 'Failed to issue certificate',
      },
    };
  }
};

// ============================================================================
// SERVICE FUNCTIONS - Certificate Viewing & Downloading
// ============================================================================

/**
 * View certificate (opens PDF inline in new tab with authentication)
 * @param enrollmentId - ID of the enrollment
 */
export const viewCertificate = async (enrollmentId: number): Promise<void> => {
  try {
    const token = localStorage.getItem('authToken');
    const baseURL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';
    const url = `${baseURL}${ENDPOINTS.VIEW_CERTIFICATE(enrollmentId)}`;

    const response = await axios.get(url, {
      responseType: 'blob',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    // Create blob URL and open in new tab
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const blobUrl = URL.createObjectURL(blob);
    window.open(blobUrl, '_blank');

    // Clean up blob URL after a delay
    setTimeout(() => URL.revokeObjectURL(blobUrl), 100);
  } catch (error: any) {
    console.error('Error viewing certificate:', error);
    throw new Error(error.response?.data?.message || 'Failed to view certificate');
  }
};

/**
 * Download certificate (downloads PDF file with authentication)
 * @param enrollmentId - ID of the enrollment
 */
export const downloadCertificate = async (enrollmentId: number): Promise<void> => {
  try {
    const token = localStorage.getItem('authToken');
    const baseURL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';
    const url = `${baseURL}${ENDPOINTS.DOWNLOAD_CERTIFICATE(enrollmentId)}`;

    const response = await axios.get(url, {
      responseType: 'blob',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    // Create blob and trigger download
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const blobUrl = URL.createObjectURL(blob);

    // Create a temporary link and trigger download
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = `Certificate_${enrollmentId}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Clean up blob URL
    URL.revokeObjectURL(blobUrl);
  } catch (error: any) {
    console.error('Error downloading certificate:', error);
    throw new Error(error.response?.data?.message || 'Failed to download certificate');
  }
};
